import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Loader from "../../../components/Loader/Index.jsx";
import TitleSubtitlePair from "../../../components/_utils/TitleSubtitlePair/index.jsx";
import SwitcherTabs from "../../../components/_utils/CommonButtons/SwitcherTabs/index.jsx";
import CommonDetailCard from "../../../components/_utils/CommonDetailCard/index.jsx";
import {
  specificNotification,
  markNotificationRead,
  snoozeNotification,
  unsnoozeNotification,
  updateNotificationInStore,
  favouriteNotification,
} from "../../../redux/features/Notifications/index.js";
import TopNavSmallButton from "../../../components/_utils/CommonButtons/TopNavSmallButton/index.jsx";
import ICONS from "../../../assets/Icons/index.js";
import SnoozePopup from "../../../components/_utils/SnoozePopup";
import {
  GlobalConfiguration,
  IntervalConfiguration,
  TimelineConfiguration,
} from "../../../utils/configurationProvider.js";
import formatDateViewAll from "../../../utils/formatDateViewAll.js";
import nameConversion from "../../../utils/nameConversion.js";
import TopNavComponent from "../../../components/_utils/CommonTopNav/TopNavComponent/index.jsx";
import LargePopupRedirect from "../../../components/_utils/CommonPopups/CommonLargePopupRedirect/index.jsx";
import useDynamicRouting from "../../../utils/useDynamicRouting.js";
import CommonToast from "../../../components/_utils/CommonToast";
import ChannelViewAll from "../../Channels/ChannelViewall/index.jsx";

// Import utils

const NotificationDetails = ({ popupNotificationId, popupMode }) => {
  // Get the notification ID from the route when the component is opened through the normal notification detail page.
  const { id: notificationId } = useParams();

  // Prefer the notification ID supplied through popup props.
  // If no popup ID is supplied, fall back to the route parameter.
  const id = popupNotificationId || notificationId;

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Controls whether the snooze popup is currently visible.
  const [showSnoozePopup, setShowSnoozePopup] = useState(false);
  //controls whether the channel view all popup is currently visible.
  const [isChannelViewAllOpen, setIsChannelViewAllOpen] = useState(false);
  const scrollRef = useRef(null);

  // Stores the currently selected Details/Channels tab index.
  // Index 0 represents Details and index 1 represents Channels.
  const [tabIndex, setTabIndex] = useState(GlobalConfiguration.lengthCheckZero);

  // Stores the CSS animation class used when switching between tabs.
  const [animationClass, setAnimationClass] = useState("");

  // Stores the previously active tab index so tab-switch direction can be determined for the animation.
  const [prevIndex, setPrevIndex] = useState(
    GlobalConfiguration?.lengthCheckZero,
  );

  // Tracks whether the notification ID has successfully been copied. Used to change the copy icon after copying.
  const [copiedNotificationId, setCopiedNotificationId] = useState(false);

  // Stores the tooltip text displayed for the notification ID copy button.
  const [tooltip, setTooltip] = useState("Copy to clipboard");

  // Retrieve the currently loaded specific notification and its loading state from the notification Redux slice.
  const { specificNotificationData, specificNotificationLoading } = useSelector(
    (state) => state?.notificationsSlice || {},
  );

  // Start with notification data returned by the specific-notification API.
  let notificationDetailsData = specificNotificationData;

  // Retrieve notification data passed through navigation state. This is useful when navigating from the notification popup to details.
  const notificationFromState = location?.state?.notification;

  // If the API has finished loading but did not return notification data,
  // use the notification passed through navigation state as a fallback.
  if (
    !specificNotificationLoading &&
    !specificNotificationData &&
    notificationFromState
  ) {
    notificationDetailsData = { notifications: [notificationFromState] };
  }

  // Custom hook used to dynamically navigate to the item associated with a notification.
  const { handleDynamicRouting } = useDynamicRouting();

  // Opens the item associated with the current notification. In popup mode, dynamic navigation is restricted because the detail page is being displayed inside another popup.
  const handleDynamicRoute = () => {
    if (popupMode) {
      actionRestricted();
      return;
    }
    handleDynamicRouting(
      notificationDetailsData?.notifications[
        GlobalConfiguration.lengthCheckZero
      ]?.eventable_type,
      notificationDetailsData?.notifications[
        GlobalConfiguration.lengthCheckZero
      ]?.eventable_id ||
        notificationDetailsData?.notifications[
          GlobalConfiguration.lengthCheckZero
        ]?.eventable_ids,

      notificationDetailsData?.notifications[
        GlobalConfiguration.lengthCheckZero
      ]?.title,
    );
  };

  //Builds the subtitle displayed below the notification message.
  const getSubtitleText = () => {
    const type =
      notificationDetailsData?.notifications?.[
        GlobalConfiguration.lengthCheckZero
      ]?.eventable_type;
    const lastSeg = type
      ?.split("_")
      ?.slice(GlobalConfiguration.navigate_back)?.[
      GlobalConfiguration.lengthCheckZero
    ];
    return lastSeg ? `View ${lastSeg}` : "Notification";
  };

  // Fetches the selected notification when the notification ID changes. After fetching, an unread notification is automatically marked as read.
  // The updated notification is then synchronized with Redux state.
  useEffect(() => {
    const getNotification = async () => {
      try {
        const response = await dispatch(specificNotification({ id }));

        const fetchedNotification =
          response?.payload?.notifications?.[
            GlobalConfiguration.lengthCheckZero
          ] || notificationFromState;

        const isRead =
          fetchedNotification?.read === true ||
          fetchedNotification?.read === "true";

        if (fetchedNotification && !isRead) {
          const idToMark =
            fetchedNotification?.id || fetchedNotification?.notification_id;
          if (idToMark) {
            const markResp = await dispatch(
              markNotificationRead({ id: idToMark, read: true }),
            );
            dispatch(
              updateNotificationInStore({
                response: markResp?.payload,
                stateToUpdate: "specificNotificationData",
              }),
            );
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    getNotification();
  }, [id]);

  //Snoozes the current notification until the selected date/time.
  const handleSnoozeConfirm = async (idProp, snoozed_until) => {
    if (popupMode) {
      actionRestricted();
      return;
    }

    const idToUse =
      idProp ||
      notificationDetailsData?.notifications?.[
        GlobalConfiguration.lengthCheckZero
      ]?.id ||
      notificationDetailsData?.notifications?.[
        GlobalConfiguration.lengthCheckZero
      ]?.notification_id;
    if (!idToUse) return;

    try {
      const response = await dispatch(
        snoozeNotification({ id: idToUse, snoozed_until }),
      );

      if (!response?.payload?.status) {
        CommonToast({
          text: "Failed to snooze notification.",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });

        return response;
      }

      dispatch(
        updateNotificationInStore({
          response: response?.payload,
          stateToUpdate: "specificNotificationData",
        }),
      );

      CommonToast({
        text: "Notification snoozed successfully.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
        buttonNeeded: false,
      });

      setShowSnoozePopup(false);

      navigate("/home/notification_dashboard/view_notifications", {
        state: {
          isSnoozeView: true,
        },
      });

      return response;
    } catch (error) {
      console.error("Failed to snooze notification:", error);

      CommonToast({
        text: "Failed to snooze notification.",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });

      return error;
    }
  };

  // Removes the snooze from the current notification.
  const handleUnsnoozeNotification = async () => {
    if (popupMode) {
      actionRestricted();
      return;
    }

    const item =
      notificationDetailsData?.notifications?.[
        GlobalConfiguration.lengthCheckZero
      ];
    const idToUnsnooze = item?.id || item?.notification_id;

    if (!idToUnsnooze) return;
    const response = await dispatch(unsnoozeNotification({ id: idToUnsnooze }));
    dispatch(
      updateNotificationInStore({
        response: response?.payload,
        stateToUpdate: "specificNotificationData",
      }),
    );
    return response;
  };

  //Toggles the favourite state of the current notification
  const handleMarkFavourite = async () => {
    if (popupMode) {
      actionRestricted();
      return;
    }

    const item =
      notificationDetailsData?.notifications?.[
        GlobalConfiguration.lengthCheckZero
      ];

    const idToFavourite = item?.id || item?.notification_id;
    if (!idToFavourite) return;
    const isFavourite = item?.favourite === true || item?.favourite === "true";
    const response = await dispatch(
      favouriteNotification({ id: idToFavourite, favourite: !isFavourite }),
    );
    dispatch(
      updateNotificationInStore({
        response: response?.payload,
        stateToUpdate: "specificNotificationData",
      }),
    );
    return response;
  };

  //Copies the current notification ID to the user's clipboard.
  const handleCopyNotificationId = async () => {
    const notificationId =
      notificationDetailsData?.notifications?.[
        GlobalConfiguration.lengthCheckZero
      ]?.id;

    if (!notificationId) return;

    try {
      await navigator.clipboard.writeText(String(notificationId));
      setCopiedNotificationId(true);
      setTooltip("Copied");
      setTimeout(() => {
        setCopiedNotificationId(false);
        setTooltip("Copy to clipboard");
      }, IntervalConfiguration.intervalMedium);
    } catch (error) {
      console.error("Failed to copy notification ID:", error);
    }
  };

  // Resets the active tab to the Details tab.
  const toggleDetail = () => {
    setTabIndex(GlobalConfiguration.lengthCheckZero);
  };

  // Extract the notification's eventable type.
  const eventableType =
    notificationDetailsData?.notifications?.[
      GlobalConfiguration.lengthCheckZero
    ]?.eventable_type;

  //Convert the eventable type into a user-friendly display value.
  const formattedEventableType = eventableType
    ? (() => {
        const converted = nameConversion(eventableType);

        if (converted !== eventableType) {
          return converted;
        }

        return eventableType
          .replaceAll("_", " ")
          .replace(/^./, (char) => char.toUpperCase());
      })()
    : "";

  //Information displayed in the "Origin Details" card.
  let originTimelineItemList = [
    {
      content: formattedEventableType,
      heading: "Notification type",
      type: "text",
    },
    {
      content: formatDateViewAll(
        notificationDetailsData?.notifications?.[
          GlobalConfiguration.lengthCheckZero
        ]?.created_at,
        true,
      ),
      heading: "Created on",
      type: "text",
    },
  ];

  //Information displayed in the "Support information" card.
  let supportInformation = [
    {
      content:
        notificationDetailsData?.notifications?.[
          GlobalConfiguration.lengthCheckZero
        ]?.id,
      heading: "Notification ID",
      type: "textWithButton",
      iconArray: [
        {
          icon: copiedNotificationId
            ? ICONS.SELECTED_WITH_GREEN_CIRCLE
            : ICONS.COPY,
          imgColor: copiedNotificationId ? "" : "",
          onClick: handleCopyNotificationId,
          title: tooltip,
        },
      ],
    },
  ];

  //Displays an error toast when the user attempts an action that is not allowed while the notification is displayed in popup mode.
  const actionRestricted = () => {
    CommonToast({
      text: "Action restricted in popup mode.",
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
    });
  };

  // Controls the animation direction when switching between tabs.
  useLayoutEffect(() => {
    if (tabIndex !== prevIndex) {
      const direction =
        tabIndex > prevIndex ? "animate-slide-next" : "animate-slide-prev";

      setAnimationClass(direction);
      setTimeout(() => {
        setPrevIndex(tabIndex);
      }, IntervalConfiguration?.intervalSemiMid);
    }
  }, [tabIndex]);

  return (
    <div
      className={`flex flex-col bg-primary-bg ${popupMode ? "h-height-full" : "h-screen"}  overflow-y-hidden overflow-x-hidden`}
    >
      {showSnoozePopup && (
        <div className="fixed top-top-base left-left-base w-screen h-screen bg-PopUp-BackDrop bg-opacity-dark-opacity z-z-index-modal">
          <SnoozePopup
            headerTitle="snooze notification"
            secondHeaderTitle="Select the date and time to snooze this notification."
            btnOneHandleFunction={() => setShowSnoozePopup(false)}
            notificationIdProp={
              notificationDetailsData?.notifications?.[
                GlobalConfiguration.lengthCheckZero
              ]?.id ||
              notificationDetailsData?.notifications?.[
                GlobalConfiguration.lengthCheckZero
              ]?.notification_id
            }
            onConfirm={handleSnoozeConfirm}
          />
        </div>
      )}
      {isChannelViewAllOpen && (
        <LargePopupRedirect
          headerTitle="Broadcasted channels"
          closeiconhandleFunction={() => {
            setIsChannelViewAllOpen(false);
          }}
          isbottomSectionneed={false}
          bottomHeaderTextNeed={false}
          headersubtext="This notification has been broadcasted across the following channels."
        >
          <ChannelViewAll notificationId={id} popup={true} />
        </LargePopupRedirect>
      )}
      {specificNotificationLoading ? (
        <div className="flex items-center justify-center loader-container m-auto">
          <Loader />
        </div>
      ) : specificNotificationData ? (
        <>
          <div
            className={` ${popupMode ? "hidden" : "hidden Qsmd:block"} Qsmd:h-navbar-breadcrumb-height bg-primary-nav flex-shrink-0`}
          ></div>
          <div
            className={` ${popupMode ? "hidden" : ""} w-width-full flex Qsmd:hidden z-z-index-fixed-ui items-center bg-primary-nav-act justify-between`}
          >
            <TopNavComponent
              isFormattingNotRequired={true}
              topNavHeader={"Notification"}
              topNavSubText="Detail page"
            />
          </div>
          <div
            ref={scrollRef}
            className="overflow-y-auto overflow-x-hidden scroll-py-[var(--padding-md)] h-auto flex flex-col flex-1"
          >
            <div className="w-width-full bg-primary-nav">
              <div className="max-w-tab-width-normal mx-auto Qsmd:px-padding-md Qlg:px-padding-base">
                <div className="flex flex-col Qsmd:px-padding-base px-padding-md">
                  <div className="flex-1 items-stretch justify-between gap-gap-md border-b border-solid border-primary-bg flex">
                    <div className="w-full flex items-center py-padding-button-l pl-padding-sm">
                      <TitleSubtitlePair
                        title={
                          notificationDetailsData?.notifications[
                            GlobalConfiguration.lengthCheckZero
                          ]?.title
                        }
                        subtitle="Title"
                        titleColor={"text-primary"}
                        isBold={true}
                        variant="md"
                      />
                    </div>
                    <div
                      className={`Qsmd:flex hidden  bg-primary-nav-act self-stretch ${popupMode ? "opacity-light-opacity" : ""}`}
                    >
                      <>
                        <TopNavSmallButton
                          btnText={
                            notificationDetailsData?.notifications[
                              GlobalConfiguration.lengthCheckZero
                            ]?.favourite === true ||
                            notificationDetailsData?.notifications[
                              GlobalConfiguration.lengthCheckZero
                            ]?.favourite === "true" ? (
                              <>
                                Unfavourite <br /> notification
                              </>
                            ) : (
                              <>
                                Favourite <br /> notification
                              </>
                            )
                          }
                          hoverOption="white"
                          enabledBgColor="var(--primary-nav-act)"
                          enabledTextColor="var(--text-primary)"
                          icon={
                            notificationDetailsData?.notifications[
                              GlobalConfiguration.lengthCheckZero
                            ]?.favourite === true ||
                            notificationDetailsData?.notifications[
                              GlobalConfiguration.lengthCheckZero
                            ]?.favourite === "true"
                              ? ICONS.FAVOURITE_FILLED
                              : ICONS.FAVOURITE
                          }
                          handleFunction={handleMarkFavourite}
                          isDisabled={popupMode}
                        />
                      </>
                    </div>
                  </div>
                  <div className="flex-1 items-stretch Qsmd:border-b border-primary-bg flex Qsmd:flex-row flex-col justify-between">
                    <div className="w-full py-padding-sm Qsmd:pr-padding-lg Qsmd:mr-marging-xs">
                      <TitleSubtitlePair
                        hoverOption="white"
                        title={
                          notificationDetailsData?.notifications[
                            GlobalConfiguration.lengthCheckZero
                          ]?.message
                        }
                        subtitle={getSubtitleText()}
                        titleColor={"text-primary"}
                        isBold={false}
                        variant="md"
                        showArrow
                        isDisabled={popupMode}
                        onClick={() => {
                          handleDynamicRoute();
                        }}
                        textAlign={"left"}
                      />
                    </div>
                    <div className="border-t border-primary-bg w-full Qsmd:hidden"></div>
                    <div className="Qsmd:flex hidden">
                      <div className="invisible">
                        <TopNavSmallButton
                          btnText={"emptybox"}
                          hoverOption="white"
                          enabledBgColor="var(--primary-nav-act)"
                          enabledTextColor="var(--text-primary)"
                          isDisabled={popupMode}
                          icon={
                            notificationDetailsData?.notifications[
                              GlobalConfiguration.lengthCheckZero
                            ]?.favourite === true ||
                            notificationDetailsData?.notifications[
                              GlobalConfiguration.lengthCheckZero
                            ]?.favourite === "true"
                              ? ICONS.FAV_FILLED
                              : ICONS.FAV
                          }
                          handleFunction={handleMarkFavourite}
                        />
                      </div>
                      {(() => {
                        const item =
                          notificationDetailsData?.notifications?.[
                            GlobalConfiguration.lengthCheckZero
                          ] || {};
                        const nowTs = Math.floor(
                          Date.now() / TimelineConfiguration.totalSeconds,
                        );
                        const isSnoozed =
                          item?.snoozed_until &&
                          Number(item?.snoozed_until) > nowTs;

                        return (
                          <TopNavSmallButton
                            btnText={
                              isSnoozed ? (
                                <>
                                  Unsnooze <br /> notification
                                </>
                              ) : (
                                <>
                                  Snooze <br /> notification
                                </>
                              )
                            }
                            isDisabled={popupMode}
                            hoverOption="white"
                            imgColor={"var(--text-primary)"}
                            enabledBgColor="var(--primary-nav-act)"
                            enabledTextColor="var(--text-primary)"
                            icon={isSnoozed ? ICONS.UNSNOOZE : ICONS.SNOOZE}
                            handleFunction={
                              isSnoozed
                                ? handleUnsnoozeNotification
                                : () => {
                                    if (popupMode) {
                                      actionRestricted();
                                      return;
                                    }

                                    setShowSnoozePopup(true);
                                  }
                            }
                          />
                        );
                      })()}
                    </div>
                  </div>
                  <div className="py-padding-md Qsmd:py-padding-base pl-padding-sm flex justify-between items-center">
                    <TitleSubtitlePair
                      title={formatDateViewAll(
                        notificationDetailsData?.notifications[
                          GlobalConfiguration.lengthCheckZero
                        ]?.delivered_at,
                        true,
                      )}
                      subtitle={"Delivered date and time"}
                      variant="md"
                      textAlign={"left"}
                    />
                    <div className="Qsmd:flex hidden">
                      <TopNavSmallButton
                        icon={ICONS.CHANNEL_SETUP}
                        imgColor="var(--text-primary)"
                        btnText={
                          <>
                            Broadcasted <br />
                            channels
                          </>
                        }
                        hoverOption="white"
                        enabledBgColor="var(--primary-nav-act)"
                        enabledTextColor="var(--text-primary)"
                        isDisabled={popupMode}
                        handleFunction={() => setIsChannelViewAllOpen(true)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Switcher Tab & Additional Details of User */}
            <div className="bg-primary-bg w-full py-padding-md block Qsmd:hidden">
              <div className="text-text-primary pl-padding-sm py-padding-button-l text-font-sm font-semibold bg-primary-card">
                Quick Actions
              </div>
              <div>
                <div className="h-auto Qsmd:hidden flex flex-row border-b border-solid border-primary-bg">
                  <>
                    <TopNavSmallButton
                      fullWidthRequired
                      btnText={
                        notificationDetailsData?.notifications[
                          GlobalConfiguration.lengthCheckZero
                        ]?.favourite === true ||
                        notificationDetailsData?.notifications[
                          GlobalConfiguration.lengthCheckZero
                        ]?.favourite === "true" ? (
                          <>
                            Unfavourite <br /> notification
                          </>
                        ) : (
                          <>
                            Favourite <br /> notification
                          </>
                        )
                      }
                      isDisabled={popupMode}
                      hoverOption="white"
                      enabledBgColor="var(--primary-nav-act)"
                      enabledTextColor="var(--text-primary)"
                      icon={
                        notificationDetailsData?.notifications[
                          GlobalConfiguration.lengthCheckZero
                        ]?.favourite === true ||
                        notificationDetailsData?.notifications[
                          GlobalConfiguration.lengthCheckZero
                        ]?.favourite === "true"
                          ? ICONS.FAVOURITE_FILLED
                          : ICONS.FAVOURITE
                      }
                      handleFunction={handleMarkFavourite}
                    />
                  </>
                </div>
                <div className="flex flex-row border-b-border border-primary-bg w-full">
                  {(() => {
                    const item =
                      notificationDetailsData?.notifications?.[
                        GlobalConfiguration.lengthCheckZero
                      ] || {};
                    const nowTs = Math.floor(
                      Date.now() / TimelineConfiguration.totalSeconds,
                    );
                    const isSnoozed =
                      item?.snoozed_until &&
                      Number(item?.snoozed_until) > nowTs;

                    return (
                      <TopNavSmallButton
                        fullWidthRequired
                        btnText={
                          isSnoozed ? (
                            <>
                              Unsnooze <br /> notification
                            </>
                          ) : (
                            <>
                              Snooze <br /> notification
                            </>
                          )
                        }
                        hoverOption="white"
                        enabledBgColor="var(--primary-nav-act)"
                        enabledTextColor="var(--text-primary)"
                        imgColor={"var(--text-primary)"}
                        icon={isSnoozed ? ICONS.UNSNOOZE : ICONS.SNOOZE}
                        isDisabled={popupMode}
                        handleFunction={
                          isSnoozed
                            ? handleUnsnoozeNotification
                            : () => {
                                if (popupMode) {
                                  actionRestricted();
                                  return;
                                }
                                setShowSnoozePopup(true);
                              }
                        }
                      />
                    );
                  })()}
                </div>
                <div>
                  <TopNavSmallButton
                    icon={ICONS.CHANNEL_SETUP}
                    btnText={
                      <>
                        Broadcasted <br />
                        channels
                      </>
                    }
                    hoverOption="white"
                    enabledBgColor="var(--primary-nav-act)"
                    enabledTextColor="var(--text-primary)"
                    imgColor={"var(--text-primary)"}
                    isDisabled={popupMode}
                    handleFunction={() => setIsChannelViewAllOpen(true)}
                    fullWidthRequired
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 scroll-py-[var(--padding-md)]">
              <div className="flex m-auto max-w-tab-width-normal Qsmd:pt-padding-md pt-padding-base px-padding-md Qlg:px-padding-base">
                <div className="flex gap-gap-sm bg-primary-card rounded-border-radius-md w-auto Qsm:overflow-x-hidden">
                  <SwitcherTabs
                    tabs={[
                      {
                        label: "Details",
                        key: "details",
                        handler: toggleDetail,
                      },
                    ]}
                    initialActiveTab="details"
                    setIndex={setTabIndex}
                  />
                </div>
              </div>
              <div className="detail-section-trans">
                {tabIndex == GlobalConfiguration.lengthCheckZero && (
                  <>
                    <div
                      className={`${animationClass} Qlg:px-padding-base px-padding-md h-auto m-auto max-w-tab-width-normal flex Qsmd:flex-row flex-col mt-margin-md gap-gap-md pb-padding-md`}
                    >
                      <CommonDetailCard
                        cardTitle="Origin Details"
                        numCols={GlobalConfiguration?.lengthCheckOne}
                        variant="fourth"
                        entries={originTimelineItemList}
                      />
                      <CommonDetailCard
                        cardTitle="Support information"
                        numCols={GlobalConfiguration?.lengthCheckOne}
                        variant="fourth"
                        entries={supportInformation}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default NotificationDetails;
